import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useInvestorLockDocs } from './hooks/useInvestorLockDocs';
import type { ApplicationDocument } from '@/types/loan-documents';

interface InvestorLockDocsCardProps {
  encompassLoanId: string;
  isViewingSnapshot: boolean;
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

function DocRow({
  doc,
  current,
  onPreview,
  onDownload,
  onSetCurrent,
  onDelete
}: {
  doc: ApplicationDocument;
  current: boolean;
  onPreview: (d: ApplicationDocument) => void;
  onDownload: (d: ApplicationDocument) => void;
  onSetCurrent: (d: ApplicationDocument) => void;
  onDelete: (d: ApplicationDocument) => void;
}) {
  const uploadedAgo = doc.createdAt ? formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true }) : '';
  const uploader = doc.uploadedByEmail || '';
  const size = formatBytes(doc.fileSizeBytes ?? null);
  const ver = typeof doc.version === 'number' ? `V${doc.version}` : '';

  return (
    <div className={`doc-row${current ? ' current' : ''}`}>
      <div className="doc-lead">
        {current ? (
          <span className="doc-badge">
            <span className="doc-badge-dot" />
            Current · {ver}
          </span>
        ) : (
          <span className="doc-ver">{ver}</span>
        )}
      </div>
      <svg className="doc-ico" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
        <path d="M14 3v6h6" />
      </svg>
      <div className="doc-body">
        <div className="doc-title" title={doc.title || doc.fileName || ''}>
          {doc.title || doc.fileName || 'Lock document'}
        </div>
        <div className="doc-meta">
          {uploadedAgo && <span>{uploadedAgo}</span>}
          {uploader && (
            <>
              <span className="dot-sep">·</span>
              <span className="mono">{uploader}</span>
            </>
          )}
          {size !== '—' && (
            <>
              <span className="dot-sep">·</span>
              <span>{size}</span>
            </>
          )}
        </div>
      </div>
      <div className="doc-actions">
        <button type="button" className="icon-btn" title="Preview" onClick={() => onPreview(doc)} aria-label="Preview">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
        {current ? (
          <button type="button" className="icon-btn" title="Download" onClick={() => onDownload(doc)} aria-label="Download">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
          </button>
        ) : (
          <button type="button" className="text-btn" onClick={() => onSetCurrent(doc)}>
            Restore
          </button>
        )}
        <button type="button" className="icon-btn" title="Delete" onClick={() => onDelete(doc)} aria-label="Delete">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * Investor Lock Document block per the handoff — `.docs-block` with a
 * `.drop-hint` persistent drag-drop band at the top of `.docs-list`, the
 * current V3 row, and a `.versions-toggle` link-style button revealing
 * previous versions. Uses the existing `useInvestorLockDocs` hook for
 * state + queue subscriptions. Hidden entirely in snapshot mode.
 */
export function InvestorLockDocsCard({ encompassLoanId, isViewingSnapshot }: InvestorLockDocsCardProps) {
  const docs = useInvestorLockDocs(encompassLoanId);
  const [prevOpen, setPrevOpen] = useState(false);

  if (isViewingSnapshot) {
    return null;
  }

  const current = docs.docs.find((d) => d.isCurrentVersion) ?? null;
  const prev = docs.docs.filter((d) => !d.isCurrentVersion);
  const total = docs.docs.length;

  return (
    <div className="docs-block">
      <div className="docs-hdr">
        <div className="docs-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
            <path d="M14 3v6h6" />
          </svg>
          <span>Investor Lock Document</span>
          <span className="docs-count">{total}</span>
        </div>
        <input
          ref={docs.fileInputRef}
          type="file"
          accept="application/pdf"
          hidden
          onChange={docs.onFileSelect}
        />
      </div>
      <div className="docs-sub">
        Drag a PDF anywhere on the list below — it becomes current. Older versions are preserved.
      </div>

      <div
        className={`docs-list${docs.isDragging ? ' dragging' : ''}`}
        onDragOver={docs.onDragOver}
        onDragLeave={docs.onDragLeave}
        onDrop={docs.onDrop}
      >
        <button
          type="button"
          className="drop-hint"
          onClick={() => docs.fileInputRef.current?.click()}
          disabled={docs.isUploading}
        >
          <span className="dh-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M17 8l-5-5-5 5" />
              <path d="M12 3v12" />
            </svg>
          </span>
          <span className="dh-text">
            <b>{docs.isUploading ? `Uploading… ${docs.uploadProgress}%` : 'Drop new lock PDF here'}</b>
            <span className="dh-sub">or click to browse · replaces current, preserves history · PDF only, max 25 MB</span>
          </span>
          <span className="dh-shortcut">
            <span className="kbd">⌘</span>
            <span className="kbd">U</span>
          </span>
        </button>

        {current && (
          <DocRow
            doc={current}
            current
            onPreview={docs.onDownload}
            onDownload={docs.onDownload}
            onSetCurrent={docs.onSetCurrent}
            onDelete={docs.onDelete}
          />
        )}

        {prev.length > 0 && (
          <button
            type="button"
            className="versions-toggle"
            aria-expanded={prevOpen}
            onClick={() => setPrevOpen((prev) => !prev)}
          >
            <svg className="vt-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span>Previous versions</span>
            <span className="vt-count">{prev.length}</span>
          </button>
        )}

        <div className="prev-rows" hidden={!prevOpen}>
          {prev.map((doc) => (
            <DocRow
              key={doc.id}
              doc={doc}
              current={false}
              onPreview={docs.onDownload}
              onDownload={docs.onDownload}
              onSetCurrent={docs.onSetCurrent}
              onDelete={docs.onDelete}
            />
          ))}
        </div>
      </div>

      <AlertDialog
        open={docs.deleteCandidate !== null}
        onOpenChange={(open) => {
          if (!open) {
            docs.clearDeleteCandidate();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {docs.deleteCandidate?.isCurrentVersion
                ? 'Delete current lock confirmation?'
                : `Delete lock V${docs.deleteCandidate?.version ?? ''}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {docs.deleteCandidate?.isCurrentVersion
                ? 'This is the current lock document. Deleting it will promote the highest remaining version to current automatically. The PDF file will be removed from storage.'
                : 'This version will be removed from the list and the PDF file deleted from storage. The current lock is unaffected.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void docs.confirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
